import { FC, useState } from "react";

import { Button, Buttons, Message, Messages } from "capitalrx-components-2.0/lib/general";
import { Dropdown, InputAlignmentContainer, RadioGroup, TextArea, Textbox } from "capitalrx-components-2.0/lib/forms";
import { Grid, Stack, Shelf, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Bold, Regular, Subheading } from "capitalrx-components-2.0/lib/typography";

import { ButtonProcessing } from "@/components";
import { TypeForm, PROVIDER_MANAGEMENT_LAYOUT } from "@/constants";
import { RoutingNumberField } from "./RoutingNumberField";
import { BankAccountNumberField } from "./BankAccountNumber";

export interface FieldsBankAccountProps {
  readonly readOnly: boolean;
  readonly typeForm: TypeForm;
}

export const FieldsBankAccount: FC<FieldsBankAccountProps> = ({ readOnly, typeForm }) => {
  const { threeFull, sixFull, twelve } = useResponsive(PROVIDER_MANAGEMENT_LAYOUT);

  const [validateAccount, setValidateAccount] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleValidateAccount = () => {
    setLoading(true);
    setTimeout(() => {
      if (Math.random() < 0.5) {
        setValidateAccount(true);
        setError(null);
      } else {
        setValidateAccount(false);
        setError("The API check has already been completed for the entered details. Modify the fields and try again.");
      }
      setLoading(false);
    }, 1000);
  };

  return (
    <Stack>
      <Subheading>Bank Account</Subheading>
      <Grid>
        <Grid.Span columns={twelve}>
          <RadioGroup.Field label="Type of Account" name="type_account" testId="type_account" orientation="horizontal" readOnly={readOnly} defaultValue="checking">
            <RadioGroup.Option value="checking">Checking</RadioGroup.Option>
            <RadioGroup.Option value="savings">Savings</RadioGroup.Option>
          </RadioGroup.Field>
        </Grid.Span>
        <Grid.Break />
        <Grid.Spread columns={sixFull}>
          <Textbox.Field label="Account Owner First Name" name="account_owner_first_name" required />
          <Textbox.Field label="Account Owner Last Name" name="account_owner_last_name" required />
          <RoutingNumberField readOnly={readOnly} />
          <BankAccountNumberField readOnly={readOnly} />
          <Stack alignment="left">
            {typeForm === "edit" && (
              <InputAlignmentContainer>
                <ButtonProcessing
                  disabled={true}
                  // disabled={readOnly}
                  loading={loading}
                  appearance={loading ? Buttons.Functional.Primary.Large : Buttons.Functional.Secondary.Large}
                  onClick={handleValidateAccount}
                  testId="provider_create_form_validate_account_button"
                >
                  validate account
                </ButtonProcessing>
              </InputAlignmentContainer>
            )}
          </Stack>
        </Grid.Spread>
        <Grid.Break />
        <Grid.Spread columns={twelve}>
          {error && (
            <Message appearance={Messages.Error} visible>
              <Shelf>
                <Regular>{error}</Regular>
                <Shelf.Spacer />
                <Button appearance={Buttons.Functional.Tertiary.Large}>Dismiss</Button>
              </Shelf>
            </Message>
          )}
          {validateAccount && (
            <Message appearance={Messages.Success} visible>
              Successfully validated account
            </Message>
          )}
        </Grid.Spread>
        {validateAccount && (
          <Grid.Span columns={twelve}>
            <Message appearance={Messages.Warning} visible>
              <Stack alignment="left">
                <Regular>Successfully validated account, but risk factors were detected. Please review before proceeding:</Regular>
                <Stack alignment="left" spacing="none">
                  <Shelf spacing="small">
                    <Bold>Risk Score:</Bold>
                    <Regular>68</Regular>
                  </Shelf>
                  <Shelf spacing="small">
                    <Bold>Risk Factors:</Bold>
                    <Regular>Account recently opened</Regular>
                  </Shelf>
                </Stack>
              </Stack>
            </Message>
          </Grid.Span>
        )}
        {validateAccount && (
          <Grid.Span columns={threeFull}>
            <Dropdown.Field label="Decision" name="decision" testId="decision" required>
              <Dropdown.Option value="test">Test</Dropdown.Option>
            </Dropdown.Field>
          </Grid.Span>
        )}
        <Grid.Break />
        {validateAccount && (
          <Grid.Span columns={sixFull}>
            <TextArea.Field label="Reason" name="reason" testId="reason" lines={3} />
          </Grid.Span>
        )}
      </Grid>
    </Stack>
  );
};
